import { Bar } from 'react-chartjs-2'

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

import { AppLanguage } from '~/types/locale'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend)

interface Props {
  dailyUseData: number[]
  max: number
  zeroData: boolean
  initialText: string[]
  language: AppLanguage
}

const WeeklyChartBar = (props: Props): React.ReactElement => {
  const { dailyUseData, zeroData, max, initialText, language } = props
  const Days = {
    [AppLanguage.EN]: ['M', 'T', 'W', 'TH', 'F', 'S', 'S'],
    [AppLanguage.NO]: ['M', 'T', 'O', 'TO', 'F', 'L', 'S'],
    [AppLanguage.UA]: ['П', 'В', 'С', 'Ч', 'П’я', 'СY', 'Н'],
  }
  const horizontalLine = 15
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })

  // Custom plugin to draw the horizontal line
  const fifteenMinutesHorizontalLinePlugin = {
    id: 'horizontalLine',
    afterDatasetDraw: (chart: any) => {
      const {
        ctx,
        chartArea: { left, right },
        scales: { y },
      } = chart

      ctx.save()
      ctx.strokeStyle = '#e3ffa0' // Line color
      ctx.lineWidth = 4 // Line width
      ctx.beginPath()
      ctx.moveTo(left + 4, y.getPixelForValue(horizontalLine)) // Start at left edge
      ctx.lineTo(right - 4, y.getPixelForValue(horizontalLine)) // End at right edge
      ctx.stroke()
      ctx.restore()
    },
  }

  // Custom Plugin for "avg" text
  const customTextPlugin = {
    id: 'customAvgText',
    afterDatasetsDraw(chart: any) {
      const {
        ctx,
        chartArea: { right },
        scales: { y },
      } = chart

      // Make sure the context exists before drawing
      if (!ctx) return
      const drawTextWithBackground = (text: string, x: number, yPos: number): void => {
        const paddingX = 10
        const borderRadius = 15
        const textWidth = ctx.measureText(text).width
        const rectWidth = textWidth + paddingX * 2
        const rectHeight = 28

        // Calculate new X position to center the text relative to the right edge
        const adjustedX = x - rectWidth / 2 // Shift left to center align

        ctx.beginPath()
        ctx.fillStyle = 'white'
        ctx.strokeStyle = '#FF9B05'
        ctx.lineWidth = 1.5

        // Draw rounded rectangle
        ctx.roundRect(adjustedX, yPos - rectHeight / 2, rectWidth, rectHeight, borderRadius)
        ctx.fill()
        ctx.stroke()

        // Draw centered text
        ctx.fillStyle = '#474141'
        ctx.fillText(text, adjustedX + paddingX, yPos + 4)
      }

      drawTextWithBackground('15 min', right / 2, y.getPixelForValue(horizontalLine)) // Adjusted x to move left

      ctx.save()
      ctx.restore()
    },
  }

  const emptyDataTextPlugin = {
    id: 'emptyDataText',
    beforeDraw(chart: any) {
      const { width, height, ctx } = chart
      const textLines = initialText

      ctx.save()
      const fontSize = 14 // Font size for the text
      ctx.font = `500 italic ${fontSize}px Poppins `
      ctx.fillStyle = 'white'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      const textX = width / 2
      const textY = height / 2

      // Calculate the starting Y position for multiline text
      const lineHeight = fontSize * 1.5
      const startY = textY - ((textLines.length - 1) / 2) * lineHeight - 10
      // Draw each line of text
      textLines.forEach((line, index) => {
        ctx.fillText(line, textX, startY + index * lineHeight)
      })

      ctx.restore()
    },
  }

  const data = {
    labels: Days[language],
    datasets: [
      {
        label: 'Minutes spent',
        data: dailyUseData,
        backgroundColor: dailyUseData.map((value) => (value === 0 ? 'transparent' : '#7D38D6')),
        borderRadius: 6,
        barThickness: isMobile ? 38 : 52,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        titleFont: {
          family: 'Poppins, sans-serif',
        },
        bodyFont: {
          family: 'Poppins, sans-serif',
        },
        footerFont: {
          family: 'Poppins, sans-serif',
        },
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      customAvgText: {
        display: true,
      },
    },
    scales: {
      y: {
        display: false,
        position: 'right',
        min: 0,
        max: max !== 15 ? max : max + 1,
        grid: {
          display: false,
        },
        ticks: {
          width: 2,
          display: false,
        },
        border: { color: '#7D38D6' },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: 'white',
          font: {
            size: isMobile ? 12 : 16,
            family: 'Poppins, sans-serif',
          },
          padding: 10,
        },
        border: { display: false },
      },
    },
    layout: {
      padding: {
        top: 12,
        left: 2,
        right: 2,
      },
    },
  }
  if (zeroData) {
    ChartJS.register(emptyDataTextPlugin)
  } else {
    ChartJS.unregister(emptyDataTextPlugin)
  }

  return (
    <div className="w-full md:w-4/5 lg:w-full h-34 md:h-48 mx-auto xl:pb-1">
      <Bar data={data} options={options} plugins={[fifteenMinutesHorizontalLinePlugin, customTextPlugin]} />
    </div>
  )
}

export default WeeklyChartBar
